import React, { useState, useEffect } from 'react';

// Main App component (representing the student's assignment details and submission view)
export default function Homework() {
  // Dummy Assignment Data - This would typically come from an API call based on assignmentId
  const [assignment, setAssignment] = useState({
    id: 'hw001',
    title: 'Algebraic Equations Worksheet',
    className: 'Math - Grade 7',
    teacherName: 'Ms. Davis',
    description: 'Complete the attached worksheet on solving linear algebraic equations. Show all your work for full credit. Remember to simplify your answers.',
    dueDate: '2025-06-12T23:59:00',
    pointsPossible: 100,
    attachments: [
      { name: 'Algebra_Worksheet.pdf', url: 'https://placehold.co/100x100/A0EEFF/000?text=PDF' },
      { name: 'Equation_Examples.png', url: 'https://placehold.co/100x100/FFD700/000?text=IMG' },
    ],
    submissionType: 'quiz', // 'textInput', 'fileUpload', 'quiz', 'externalLink'
    logicBasedQuestions: [
      {
        id: 'q1',
        questionText: 'What is the value of $x$ in the equation $2x + 5 = 15$?',
        questionType: 'shortAnswer',
        // correctAnswer: '5', // Removed correct answer for student view logic
        points: 20,
      },
      {
        id: 'q2',
        questionText: 'Select the correct statement: $3^2 = 9$ or $3^2 = 6$?',
        questionType: 'multipleChoice',
        options: [{ text: '$3^2 = 9$', isCorrect: true }, { text: '$3^2 = 6$', isCorrect: false }],
        points: 15,
      },
      {
        id: 'q3',
        questionText: 'Implement a Python function to calculate the Fibonacci sequence up to n terms.',
        questionType: 'codeSnippet',
        codeTemplate: 'def fibonacci(n):\n    # Your code here\n    pass',
        points: 40,
      },
      {
        id: 'q4',
        questionText: 'Is the following statement true or false: All squares are rectangles.',
        questionType: 'trueFalse',
        // correctAnswer: 'True', // Removed correct answer for student view logic
        points: 10,
      },
      {
        id: 'q5',
        questionText: 'Solve for $y$: $y^2 - 4y + 4 = 0$',
        questionType: 'mathProblem',
        mathProblem: 'Solve for $y$: $y^2 - 4y + 4 = 0$',
        answerFormat: 'equation', // numerical, equation, expression
        // correctAnswer: 'y=2', // Removed correct answer for student view logic
        points: 15,
      },
    ],
  });

  // State for student's submission
  const [studentSubmission, setStudentSubmission] = useState(() => {
    // Initialize quizAnswers based on assignment questions
    const initialQuizAnswers = assignment.logicBasedQuestions.map(q => {
      let initialAnswer = '';
      if (q.questionType === 'multipleChoice' && q.options.length > 0) {
        // For multiple choice, initialize with an empty string or the first option's text if preferred
        initialAnswer = '';
      }
      return { questionId: q.id, answer: initialAnswer };
    });

    return {
      textInput: '',
      uploadedFiles: [],
      quizAnswers: initialQuizAnswers,
      externalLink: '',
      isSubmitted: false,
      isSavedDraft: false,
    };
  });

  // State for managing UI (e.g., loading, alerts)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'

  // Countdown timer logic
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const due = new Date(assignment.dueDate);
      const diffMs = due.getTime() - now.getTime();

      if (diffMs < 0) {
        return { text: 'Overdue', color: 'text-red-600 font-bold' };
      }

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffDays > 0) {
        return { text: `${diffDays} days, ${diffHours} hours left`, color: 'text-orange-500' };
      } else if (diffHours > 0) {
        return { text: `${diffHours} hours, ${diffMinutes} mins left`, color: 'text-red-500' };
      } else {
        return { text: `${diffMinutes} minutes left!`, color: 'text-red-600 font-bold' };
      }
    };

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000); // Update every second for live countdown

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    return () => clearInterval(timer);
  }, [assignment.dueDate]);

  // Handle changes for text input submission
  const handleTextInputChange = (e) => {
    setStudentSubmission({ ...studentSubmission, textInput: e.target.value });
  };

  // Handle file upload submission
  const handleFileUploadChange = (e) => {
    const files = Array.from(e.target.files);
    setStudentSubmission({ ...studentSubmission, uploadedFiles: [...studentSubmission.uploadedFiles, ...files] });
  };

  // Handle remove uploaded file
  const handleRemoveUploadedFile = (fileName) => {
    setStudentSubmission(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(file => file.name !== fileName)
    }));
  };

  // Handle quiz answer changes
  const handleQuizAnswerChange = (questionId, value) => { // Removed questionType as it's not needed here
    setStudentSubmission(prev => ({
      ...prev,
      quizAnswers: prev.quizAnswers.map(qa =>
        qa.questionId === questionId
          ? { ...qa, answer: value }
          : qa
      )
    }));
  };

  // Handle external link change
  const handleExternalLinkChange = (e) => {
    setStudentSubmission({ ...studentSubmission, externalLink: e.target.value });
  };

  // Generic message display handler
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000); // Message disappears after 5 seconds
  };

  // Handle Save Draft
  const handleSaveDraft = () => {
    // In a real app, this would send data to a backend
    console.log('Saving Draft:', studentSubmission);
    setStudentSubmission(prev => ({ ...prev, isSavedDraft: true }));
    showMessage('Draft saved successfully!', 'success');
  };

  // Handle Submit Confirmation
  const confirmSubmission = () => {
    setShowConfirmation(true);
  };

  // Handle Actual Submission
  const handleSubmitAssignment = () => {
    setShowConfirmation(false);
    if (studentSubmission.isSubmitted) {
      showMessage('Assignment already submitted!', 'info');
      return;
    }
    // In a real app, this would send data to a backend
    console.log('Submitting Assignment:', studentSubmission);
    setStudentSubmission(prev => ({ ...prev, isSubmitted: true }));
    showMessage('Assignment submitted successfully!', 'success');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white-50 via-white-50 to-pink-50 px-2 py-10 sm:px-4 lg:px-10 font-['Nunito'] overflow-hidden">
   
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center transform scale-95 animate-scale-in">
            <h3 className="text-2xl font-bold text-indigo-700 mb-4">Confirm Submission</h3>
            <p className="text-gray-700 mb-6">Are you sure you want to submit this assignment? You will not be able to edit it after submission.</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-300 shadow-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAssignment}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-md font-medium"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Box */}
      {message && (
        <div className={`fixed top-6 right-6 p-4 rounded-lg shadow-xl text-white z-50 transform translate-x-0 transition-all duration-500 ease-out animate-slide-in-right
          ${messageType === 'success' ? 'bg-green-600' : messageType === 'error' ? 'bg-red-600' : 'bg-blue-600'}`}>
          {message}
        </div>
      )}

      {/* Main Assignment Card */}
      <main className=" mx-auto bg-white  rounded-2xl shadow-2xl border-t-8 border-indigo-600 animate-fade-in-up">
        <h1 className="text-4xl font-extrabold text-gray-900 mb-2 leading-tight">{assignment.title}</h1>
        <p className="text-xl text-indigo-700 font-medium mb-1">{assignment.className}</p>
        <p className="text-md text-gray-600 mb-6">Assigned by: <span className="font-semibold">{assignment.teacherName}</span></p>

        {/* Assignment Info Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-indigo-50 p-5 rounded-xl mb-8 shadow-inner">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Due Date:</p>
            <p className="text-base sm:text-lg font-semibold text-indigo-800">
              {new Date(assignment.dueDate).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Points:</p>
            <p className="text-base sm:text-lg font-semibold text-indigo-800">{assignment.pointsPossible || 'N/A'}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Time Remaining:</p>
            <p className={`text-base sm:text-lg font-bold ${timeRemaining.color}`}>
              {timeRemaining.text}
            </p>
          </div>
        </div>

        {/* Description Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <svg className="w-6 h-6 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
            Description
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">{assignment.description}</p>
        </div>

        {/* Attachments Section */}
        {assignment.attachments && assignment.attachments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
              Attachments
            </h2>
            <div className="flex flex-wrap gap-4">
              {assignment.attachments.map((file, index) => (
                <a
                  key={index}
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 bg-indigo-100 text-indigo-800 px-5 py-3 rounded-xl hover:bg-indigo-200 transition-colors duration-300 shadow-sm hover:shadow-md font-medium text-base"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  <span>{file.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <hr className="my-10 border-gray-200" />

        {/* Your Submission Section */}
        <h2 className="text-3xl font-bold text-indigo-700 mb-8 text-center">Your Submission</h2>

        {/* Conditional Submission Inputs */}
        {assignment.submissionType === 'textInput' && (
          <div className="mb-8">
            <label htmlFor="studentTextInput" className="block text-xl font-semibold text-gray-700 mb-3">Your Answer</label>
            <textarea
              id="studentTextInput"
              rows="10"
              className={`mt-1 block w-full p-4 border border-gray-300 rounded-lg shadow-sm text-lg
                focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200
                ${studentSubmission.isSubmitted ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
              placeholder="Type your answer here..."
              value={studentSubmission.textInput}
              onChange={handleTextInputChange}
              disabled={studentSubmission.isSubmitted}
            ></textarea>
          </div>
        )}

        {assignment.submissionType === 'fileUpload' && (
          <div className="mb-8">
            <label className="block text-xl font-semibold text-gray-700 mb-3">Upload Your Files</label>
            <div
              className={`mt-1 flex flex-col items-center justify-center px-6 pt-5 pb-6 border-2 border-indigo-300 border-dashed rounded-xl cursor-pointer transition-all duration-300
                ${studentSubmission.isSubmitted ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'hover:border-indigo-500 hover:bg-indigo-50'}`}
              onClick={() => !studentSubmission.isSubmitted && document.getElementById('student-file-upload').click()}
            >
              <div className="text-center p-4">
                <svg className="mx-auto h-16 w-16 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                <p className="mt-3 text-lg text-gray-700">
                  <span className="font-bold text-indigo-600">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-gray-500 mt-1">Documents, images, videos, etc.</p>
              </div>
              <input id="student-file-upload" type="file" className="sr-only" multiple onChange={handleFileUploadChange} disabled={studentSubmission.isSubmitted} />
            </div>
            {studentSubmission.uploadedFiles.length > 0 && (
              <div className="mt-6 space-y-3">
                <p className="text-lg font-medium text-gray-700">Your Uploaded Files:</p>
                {studentSubmission.uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-100 p-3 rounded-lg text-base text-gray-800 shadow-sm">
                    <span className="truncate">{file.name}</span>
                    {!studentSubmission.isSubmitted && (
                      <button
                        type="button"
                        onClick={() => handleRemoveUploadedFile(file.name)}
                        className="ml-4 text-red-500 hover:text-red-700 transition-colors duration-200"
                        title="Remove file"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {assignment.submissionType === 'quiz' && (
          <div className="mb-8 space-y-8">
            <h3 className="text-2xl font-bold text-gray-800">Answer the Questions:</h3>
            {assignment.logicBasedQuestions.map((q, qIndex) => (
              <div key={q.id} className="bg-white p-6 rounded-xl shadow-md border border-blue-200 transition-all duration-300 hover:shadow-lg">
                <p className="text-xl text-gray-900 font-semibold mb-4">Q{qIndex + 1}. {q.questionText}</p>
                {q.questionType === 'shortAnswer' && (
                  <textarea
                    rows="4"
                    className={`mt-2 block w-full p-3 border border-blue-300 rounded-lg text-lg
                      focus:ring-blue-500 focus:border-blue-500 transition-all duration-200
                      ${studentSubmission.isSubmitted ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                    placeholder="Your answer..."
                    value={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer || ''}
                    onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                    disabled={studentSubmission.isSubmitted}
                  ></textarea>
                )}
                {q.questionType === 'multipleChoice' && (
                  <div className="space-y-3 mt-3">
                    {q.options.map((option, optIndex) => (
                      <label key={optIndex} className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                        <input
                          type="radio"
                          name={`mcq-${q.id}`}
                          className="form-radio h-5 w-5 text-indigo-600 focus:ring-indigo-500"
                          value={option.text}
                          checked={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer === option.text}
                          onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                          disabled={studentSubmission.isSubmitted}
                        />
                        <span className="ml-3 text-lg text-gray-800">{option.text}</span>
                      </label>
                    ))}
                  </div>
                )}
                {q.questionType === 'trueFalse' && (
                  <div className="flex space-x-6 mt-3">
                    <label className="inline-flex items-center cursor-pointer p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                      <input
                        type="radio"
                        name={`tf-${q.id}`}
                        className="form-radio h-5 w-5 text-indigo-600 focus:ring-indigo-500"
                        value="True"
                        checked={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer === 'True'}
                        onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                        disabled={studentSubmission.isSubmitted}
                      />
                      <span className="ml-2 text-lg text-gray-800">True</span>
                    </label>
                    <label className="inline-flex items-center cursor-pointer p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                      <input
                        type="radio"
                        name={`tf-${q.id}`}
                        className="form-radio h-5 w-5 text-indigo-600 focus:ring-indigo-500"
                        value="False"
                        checked={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer === 'False'}
                        onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                        disabled={studentSubmission.isSubmitted}
                      />
                      <span className="ml-2 text-lg text-gray-800">False</span>
                    </label>
                  </div>
                )}
                {q.questionType === 'codeSnippet' && (
                  <div className="mt-3">
                    <p className="text-lg font-medium text-gray-700 mb-2">Your Code:</p>
                    <textarea
                      rows="10"
                      className={`mt-1 block w-full p-4 font-mono text-base bg-gray-800 text-white rounded-lg shadow-sm
                        focus:ring-blue-500 focus:border-blue-500 transition-all duration-200
                        ${studentSubmission.isSubmitted ? 'opacity-70 cursor-not-allowed' : ''}`}
                      placeholder={q.codeTemplate || 'Write your code here...'}
                      value={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer || ''}
                      onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                      disabled={studentSubmission.isSubmitted}
                    ></textarea>
                    {/* Placeholder for Run Code / Test button */}
                    <button className="mt-4 bg-gray-700 text-white py-3 px-6 rounded-lg text-base font-medium hover:bg-gray-800 transition-colors duration-200 shadow-md" disabled={studentSubmission.isSubmitted}>
                      Run Code
                    </button>
                    <p className="text-sm text-gray-500 mt-2">You can run your code against basic tests here.</p>
                  </div>
                )}
                 {q.questionType === 'mathProblem' && (
                  <div className="mt-3">
                    <label htmlFor={`math-answer-${q.id}`} className="block text-lg font-medium text-gray-700 mb-2">Your Answer:</label>
                    <input
                      type="text"
                      id={`math-answer-${q.id}`}
                      className={`mt-1 block w-full p-3 border border-blue-300 rounded-lg text-lg
                        shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-all duration-200
                        ${studentSubmission.isSubmitted ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                      placeholder="Enter your answer (e.g., x=5 or $y=2x+1$)"
                      value={studentSubmission.quizAnswers.find(a => a.questionId === q.id)?.answer || ''}
                      onChange={(e) => handleQuizAnswerChange(q.id, e.target.value)}
                      disabled={studentSubmission.isSubmitted}
                    />
                    <p className="text-sm text-gray-500 mt-1">For equations, use LaTeX or standard mathematical notation.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {assignment.submissionType === 'externalLink' && (
          <div className="mb-8">
            <label htmlFor="externalLinkInput" className="block text-xl font-semibold text-gray-700 mb-3">External Link</label>
            <input
              type="url"
              id="externalLinkInput"
              className={`mt-1 block w-full p-4 border border-gray-300 rounded-lg text-lg
                shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200
                ${studentSubmission.isSubmitted ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
              placeholder="e.g., https://yourproject.com/my-homework"
              value={studentSubmission.externalLink}
              onChange={handleExternalLinkChange}
              disabled={studentSubmission.isSubmitted}
            />
            <p className="text-sm text-gray-500 mt-2">Paste the URL to your completed project or external submission here.</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={handleSaveDraft}
            className={`px-8 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white
              hover:bg-gray-100 transition-all duration-300 shadow-md font-semibold text-lg
              ${studentSubmission.isSubmitted ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
            disabled={studentSubmission.isSubmitted}
          >
            {studentSubmission.isSavedDraft ? 'Draft Saved!' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={confirmSubmission}
            className={`px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-lg
              hover:from-indigo-700 hover:to-purple-800 transition-all duration-300 shadow-lg font-semibold text-lg
              ${studentSubmission.isSubmitted ? 'opacity-50 cursor-not-allowed' : 'transform hover:-translate-y-1'}`}
            disabled={studentSubmission.isSubmitted}
          >
            {studentSubmission.isSubmitted ? 'Submitted!' : 'Submit Homework'}
          </button>
        </div>

        {studentSubmission.isSubmitted && (
          <p className="text-center text-green-700 font-bold text-xl mt-6 p-3 bg-green-50 rounded-lg shadow-sm">
            Your homework has been successfully submitted! 🎉
          </p>
        )}
      </main>

      {/* Custom CSS for animations */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeinUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        .animate-fade-in-up { animation: fadeinUp 0.5s ease-out forwards; }
        .animate-slide-in-right { animation: slideInRight 0.5s ease-out forwards; }
        `}
      </style>
    </div>
  );
}
